/**
 * The Toolbox permission matrix and the rules built on it (epic
 * PI-SPEC-SW-E283).
 *
 * The matrix is data, not if-chains: the SpecTree API, the Toolbox pages and
 * the Toolbox app all read {@link ROLE_CAPABILITIES}, so they cannot drift.
 *
 * | Capability             | Admin | Project Manager | Product Owner | Developer | QA Tester | Viewer |
 * |------------------------|:-----:|:---------------:|:-------------:|:---------:|:---------:|:------:|
 * | prepare-sessions       |  yes  |       yes       |       -       |     -     |     -     |   -    |
 * | manage-agenda          |  yes  |       yes       |       -       |     -     |     -     |   -    |
 * | map-project            |  yes  |       yes       |       -       |     -     |     -     |   -    |
 * | groom-one              |  yes  |       yes       |      yes      |     -     |     -     |   -    |
 * | post-single-brief      |  yes  |       yes       |      yes      |     -     |     -     |   -    |
 * | formulator             |  yes  |       yes       |      yes      |    yes    |    yes    |   -    |
 * | fogbugz                |  yes  |       yes       |      yes      |    yes    |    yes    |   -    |
 * | fogbugz-to-ado         |  yes  |       yes       |       -       |     -     |     -     |   -    |
 * | ask                    |  yes  |       yes       |      yes      |    yes    |    yes    |   -    |
 * | read-backlog           |  yes  |       yes       |      yes      |    yes    |    yes    |  yes   |
 * | read-briefs           |  yes  |       yes       |      yes      |    yes    |    yes    |  yes   |
 * | read-grooming-progress |  yes  |       yes       |      yes      |     -     |     -     |  yes   |
 * | present                |  yes  |       yes       |      yes      |     -     |     -     |   -    |
 * | assign-roles           |  yes  |       yes       |       -       |     -     |     -     |   -    |
 * | manage-repos           |  yes  |        -        |       -       |     -     |     -     |   -    |
 *
 * `read-grooming-progress` (epic PI-SPEC-SW-E293) opens the "How grooming is
 * going" metrics to Admins, Project Managers, Product Owners and Viewers but
 * never to Developers or QA Testers, which is why it is not `read-backlog`.
 *
 * `fogbugz` (epic PI-SPEC-SW-E305, ER-1662, Package A) is the gate for the
 * FogBugz tools of Packages B, C, E and F. Everyone who can use the ADO
 * Formulator can use it, Viewers cannot. No API route uses it yet.
 *
 * `fogbugz-to-ado` (epic PI-SPEC-SW-E311, ER-1666, Package E) is the gate
 * for the case → ADO Bug tool ("Bring a FogBugz case to the board"): a
 * Project Manager turns an existing FogBugz case into an ADO Bug on the
 * board. Admins and Project Managers only; Product Owners, Developers, QA
 * Testers and Viewers never hold it. Admin and Project Manager derive it
 * from `TOOLBOX_CAPABILITIES` (Project Manager excludes only `manage-repos`).
 *
 * `ask` (epic PI-SPEC-SW-E310, ER-1667, Package F) is the gate for Product
 * Q&A ("Ask about the product"): questions answered from the product's code
 * and written specs. Everyone with repo copies gets it (every role except
 * Viewer), the same rule as `fogbugz`.
 *
 * `manage-repos` (epic PI-SPEC-SW-E288) is Admin only: Admins set up the
 * repositories and the Grooming switch; a Project Manager sees the Repos page
 * read-only and keeps "Update my copies" and "Sign in to the repos".
 *
 * A Project Manager's `assign-roles` is limited to their own project and to
 * the roles {@link canAssignRole} allows; `admin` is never assigned here.
 */
import { isProjectRole, TOOLBOX_CAPABILITIES, TOOLBOX_PERSONAS, } from "./types.js";
/** The capabilities each role grants on its own. A person's capabilities are the union over their roles. */
export const ROLE_CAPABILITIES = Object.freeze({
    admin: Object.freeze([...TOOLBOX_CAPABILITIES]),
    "project-manager": Object.freeze(TOOLBOX_CAPABILITIES.filter((capability) => capability !== "manage-repos")),
    "product-owner": Object.freeze([
        "groom-one",
        "post-single-brief",
        "formulator",
        "fogbugz",
        "ask",
        "read-backlog",
        "read-briefs",
        "read-grooming-progress",
        "present",
    ]),
    developer: Object.freeze(["formulator", "fogbugz", "ask", "read-backlog", "read-briefs"]),
    "qa-tester": Object.freeze(["formulator", "fogbugz", "ask", "read-backlog", "read-briefs"]),
    viewer: Object.freeze(["read-backlog", "read-briefs", "read-grooming-progress"]),
});
/** The capabilities `role` grants; none for a value that is not a Toolbox role (input from outside TypeScript). */
function grantsOf(role) {
    return ROLE_CAPABILITIES[role] ?? [];
}
/** The union of the capabilities of `roles`, in {@link TOOLBOX_CAPABILITIES} order. No roles, no capabilities. */
export function capabilitiesFor(roles) {
    const granted = new Set();
    for (const role of roles) {
        for (const capability of grantsOf(role))
            granted.add(capability);
    }
    return TOOLBOX_CAPABILITIES.filter((capability) => granted.has(capability));
}
/** True when any of `roles` grants `capability`. */
export function hasCapability(roles, capability) {
    return roles.some((role) => grantsOf(role).includes(capability));
}
/** The behaviour each role gives the tools; `viewer` uses no tool that has one. */
export const ROLE_PERSONA = Object.freeze({
    admin: "product-owner",
    "project-manager": "product-owner",
    "product-owner": "product-owner",
    developer: "developer",
    "qa-tester": "qa-tester",
    viewer: null,
});
/**
 * The personas a person with `roles` may choose from, distinct and in
 * {@link TOOLBOX_PERSONAS} order. An Admin may choose any persona (so they
 * can test as a QA Tester). Empty when no role carries one (Viewer only, or
 * no roles).
 */
export function personasFor(roles) {
    if (roles.includes("admin"))
        return [...TOOLBOX_PERSONAS];
    const allowed = new Set();
    for (const role of roles) {
        const persona = ROLE_PERSONA[role];
        if (persona)
            allowed.add(persona);
    }
    return TOOLBOX_PERSONAS.filter((persona) => allowed.has(persona));
}
/** The roles a Project Manager may assign or remove inside their own project. */
const PROJECT_MANAGER_ASSIGNABLE = ["product-owner", "developer", "qa-tester", "viewer"];
/**
 * True when someone with `assignerRoles` on a project may assign `target` on
 * it:
 * - an Admin may assign any per-project role (`PROJECT_ROLES`);
 * - a Project Manager may assign Product Owner, Developer, QA Tester and
 *   Viewer, but not Project Manager;
 * - nobody may assign `admin`: it comes from `User.isGlobalAdmin` only.
 */
export function canAssignRole({ assignerRoles, target }) {
    if (target === "admin")
        return false;
    if (!isProjectRole(target))
        return false;
    if (assignerRoles.includes("admin"))
        return true;
    if (assignerRoles.includes("project-manager"))
        return PROJECT_MANAGER_ASSIGNABLE.includes(target);
    return false;
}
/** True when someone with `assignerRoles` may remove `target` from a person on the project. Same rule as {@link canAssignRole}. */
export function canRemoveRole(check) {
    return canAssignRole(check);
}
//# sourceMappingURL=matrix.js.map